/**
 * Phase P — Per-section FG WMS Landing.
 *
 * Section-aware clone of `pages/WMS/FGWMSDashboard.jsx`. Mounted at
 * `/sections/dispatch/:section/fg-wms`. All tiles route inside the
 * per-section URL space so reads/writes stay isolated.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Box, BoxSelect, Smartphone, BarChart3, FileText, AlertTriangle, Truck, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import apiClient from '@/services/apiClient';
import { filterOperations } from '@/utils/operationAccess';

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo',
  art_silk: 'Art Silk',
  kids: 'Kids',
};

const PerSectionFgWmsLanding = () => {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const sectionName = SLUG_TO_DISPLAY[slug];
  const basePath = `/sections/dispatch/${slug}`;
  const apiBase = `/per-section/${slug}`;

  const [stats, setStats] = useState({ fgBundles: 0, pendingPickSlips: 0 });

  const fetchStats = useCallback(async () => {
    try {
      const [invRes, pendingRes] = await Promise.all([
        apiClient.get(`${apiBase}/fg-inventory`).catch(() => ({ data: { total: 0 } })),
        apiClient.get(`${apiBase}/fg-pick/pending`).catch(() => ({ data: { total: 0, items: [] } })),
      ]);
      setStats({
        fgBundles: invRes.data?.total ?? (Array.isArray(invRes.data?.items) ? invRes.data.items.length : 0),
        pendingPickSlips: pendingRes.data?.total ?? (Array.isArray(pendingRes.data?.items) ? pendingRes.data.items.length : 0),
      });
    } catch (err) {
      console.error('[per-section/fg-wms] fetchStats error:', err);
    }
  }, [apiBase]);

  useEffect(() => {
    if (slug) fetchStats();
  }, [slug, fetchStats]);

  if (!sectionName) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FFFBF5] p-6">
        <AlertTriangle className="w-10 h-10 text-amber-500 mb-3" />
        <h2 className="text-lg font-bold">Unknown section</h2>
        <p className="text-sm text-gray-500 mt-1">No catalog entry for slug <code className="font-mono">{slug}</code>.</p>
      </div>
    );
  }

  const modules = [
    { id: 'fg-put-away',    title: 'FG Put-Away',    description: 'Put away finished packs into FG bins', icon: BoxSelect, route: `${basePath}/fg-put-away`,    color: 'bg-teal-500' },
    { id: 'fg-pick',        title: 'FG Pick',        description: 'Pick finished packs from FG bins',     icon: Truck,     route: `${basePath}/fg-pick`,        color: 'bg-amber-500' },
    { id: 'fg-mobile-pa',   title: 'FG Put-Away (Mobile)', description: 'Touch-optimised put-away UI',    icon: Smartphone, route: `${basePath}/mobile/fg-put-away`, color: 'bg-emerald-600' },
    { id: 'fg-mobile-pick', title: 'FG Pick (Mobile)',     description: 'Touch-optimised picking UI',     icon: Smartphone, route: `${basePath}/mobile/fg-pick`,     color: 'bg-emerald-600' },
    { id: 'combo-v2',       title: 'ComboV2 Plans',     description: 'Plan & distribute combo runs',      icon: ClipboardList, route: `${basePath}/combo-v2`,     color: 'bg-fuchsia-500' },
    { id: 'pp-station',     title: 'PP Station',        description: 'Scan USNs to assemble combos',      icon: ClipboardList, route: `${basePath}/pp-station`,   color: 'bg-cyan-600' },
    { id: 'footprint-report', title: 'Footprint Movement', description: 'Put-away & picking movement report', icon: BarChart3, route: `${basePath}/footprint-movement-report`, color: 'bg-indigo-500' },
    { id: 'closing-stock',    title: 'Closing Stock Report', description: 'Live closing stock by operation',  icon: FileText, route: `${basePath}/closing-stock`, color: 'bg-teal-600' },
  ];

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid={`per-section-fg-wms-${slug}`}>
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate(basePath)}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Box className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold font-display">FG WMS</h1>
                <p className="text-sm text-[#E8DCC8]">{sectionName} · Finished Goods</p>
              </div>
            </div>
          </div>
          <Button
            onClick={() => navigate(`${basePath}/mobile`)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="mobile-view-btn"
          >
            <Smartphone className="w-4 h-4 mr-2" />
            Mobile View
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6" data-testid="stat-fg-bundles">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">FG Bundles in Storage</p>
                <p className="text-3xl font-bold text-[#1A1F2E] mt-1">{stats.fgBundles}</p>
              </div>
              <Box className="w-10 h-10 text-teal-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6" data-testid="stat-pending-pick-slips">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">Pending Pick Slips</p>
                <p className="text-3xl font-bold text-[#1A1F2E] mt-1">{stats.pendingPickSlips}</p>
              </div>
              <ClipboardList className="w-10 h-10 text-amber-500" />
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-[#1A1F2E] mb-6 font-display">FG Operations · {sectionName}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filterOperations(`dispatch_${slug}`, modules).map((m) => {
            const Icon = m.icon;
            return (
              <div
                key={m.id}
                onClick={() => navigate(m.route)}
                className="bg-white rounded-lg border-2 border-[#E5DFD0] hover:border-[#2D5F4F] shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden"
                data-testid={`fg-wms-module-${m.id}`}
              >
                <div className={`${m.color} p-4`}>
                  <Icon className="w-10 h-10 text-white" />
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-[#1A1F2E] mb-1 font-display">{m.title}</h3>
                  <p className="text-sm text-[#5A5F6E]">{m.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default PerSectionFgWmsLanding;
